import { useState } from 'react'
import { useAdminEmployees, AdminUser } from '@/hooks/useAdminUsers'
import UserDetailDrawer from './UserDetailDrawer'

export default function EmployeesTab() {
  const [search, setSearch] = useState('')
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)

  const { data, isLoading, error } = useAdminEmployees({
    search: search || undefined,
  })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[#e8eaed]">Employees</h2>
          <p className="text-sm text-[#8b949e]">Manage Personic employee permissions</p>
        </div>

        {/* Search */}
        <div className="w-full sm:w-64">
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full px-3 py-2 bg-[#141c24] border border-[#2a3542] rounded-lg text-[#e8eaed] placeholder-[#8b949e] text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="bg-[#141c24] border border-[#2a3542] rounded-lg p-12 text-center text-[#8b949e]">
          <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          Loading employees...
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6">
          <p className="text-red-400">Failed to load employees. Please try again.</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && data?.users.length === 0 && (
        <div className="bg-[#141c24] border border-[#2a3542] rounded-lg p-12 text-center">
          <p className="text-[#8b949e]">No employees found</p>
        </div>
      )}

      {/* Employee Table */}
      {!isLoading && !error && data && data.users.length > 0 && (
        <div className="bg-[#141c24] border border-[#2a3542] rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-[#2a3542]">
            <thead className="bg-[#0a0f14]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#8b949e] uppercase tracking-wider">
                  Employee
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#8b949e] uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#8b949e] uppercase tracking-wider">
                  MFA
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#8b949e] uppercase tracking-wider">
                  Features
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#8b949e] uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-[#8b949e] uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#2a3542]">
              {data.users.map((user) => (
                <EmployeeRow
                  key={user.id}
                  user={user}
                  onSelect={() => setSelectedUserId(user.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Results Info */}
      {data && (
        <p className="text-sm text-[#8b949e]">
          Showing {data.users.length} of {data.total} employees
        </p>
      )}

      {/* User Detail Drawer */}
      <UserDetailDrawer userId={selectedUserId} onClose={() => setSelectedUserId(null)} />
    </div>
  )
}

function EmployeeRow({ user, onSelect }: { user: AdminUser; onSelect: () => void }) {
  return (
    <tr className="hover:bg-[#1c2630] transition-colors">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold">
            {user.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="text-sm font-medium text-[#e8eaed]">{user.name}</div>
            <div className="text-sm text-[#8b949e]">{user.email}</div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            user.is_active
              ? 'bg-green-500/20 text-green-400'
              : 'bg-red-500/20 text-red-400'
          }`}
        >
          {user.is_active ? 'Active' : 'Inactive'}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            user.mfa_enabled
              ? 'bg-green-500/20 text-green-400'
              : 'bg-yellow-500/20 text-yellow-400'
          }`}
        >
          {user.mfa_enabled ? 'Enabled' : 'Disabled'}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex flex-wrap gap-1">
          {user.feature_overrides.length > 0 ? (
            <>
              {user.feature_overrides.slice(0, 2).map((feature) => (
                <span
                  key={feature}
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-teal-500/20 text-teal-400"
                >
                  {feature}
                </span>
              ))}
              {user.feature_overrides.length > 2 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-[#2a3542] text-[#8b949e]">
                  +{user.feature_overrides.length - 2}
                </span>
              )}
            </>
          ) : (
            <span className="text-xs text-[#8b949e]">Default only</span>
          )}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-[#8b949e]">
        {user.last_login
          ? new Date(user.last_login).toLocaleDateString()
          : 'Never'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right">
        <button
          onClick={onSelect}
          className="px-3 py-1.5 text-sm font-medium text-teal-400 hover:text-teal-300 hover:bg-teal-500/10 rounded-lg transition-colors"
        >
          Manage
        </button>
      </td>
    </tr>
  )
}
